/**
 * The header's measurements and finish, resolved for rendering.
 *
 * Kept apart from the header component because three different things need
 * the same answers — the bar, the dropdown panels, and the buttons in the bar
 * — and because a header saved before any of these settings existed has to
 * keep rendering exactly as it did. Every value falls back to what the bar
 * used to hard-code.
 */

const DEFAULTS = {
  maxWidth: 1200,
  gutter: 24,
  height: 64,
  linkGap: 24,
  brandGap: 40,
  menuVariant: 'plain',
  fontSize: 15,
  uppercase: false,
  letterSpacing: 0,
  bold: false,
  buttonStyle: 'solid',
  border: true,
  shadow: false,
};

const num = (v, fallback) => (Number.isFinite(Number(v)) ? Number(v) : fallback);

export const headerStyle = (header) => {
  const s = header?.style || {};
  const glass = s.glass || {};
  return {
    maxWidth: num(s.maxWidth, DEFAULTS.maxWidth),
    gutter: num(s.gutter, DEFAULTS.gutter),
    height: num(s.height, DEFAULTS.height),
    linkGap: num(s.linkGap, DEFAULTS.linkGap),
    brandGap: num(s.brandGap, DEFAULTS.brandGap),
    menuVariant: s.menuVariant || DEFAULTS.menuVariant,
    fontSize: num(s.fontSize, DEFAULTS.fontSize),
    uppercase: Boolean(s.uppercase),
    letterSpacing: num(s.letterSpacing, DEFAULTS.letterSpacing),
    bold: Boolean(s.bold),
    buttonStyle: s.buttonStyle || DEFAULTS.buttonStyle,
    border: s.border === undefined ? DEFAULTS.border : Boolean(s.border),
    shadow: Boolean(s.shadow),
    glass: {
      enabled: Boolean(glass.enabled),
      blur: num(glass.blur, 12),
      amount: num(glass.amount, 60),
    },
    // Dropdowns get their own surface. A translucent bar is translucent on
    // purpose; a panel floating over page content has to stay readable, so it
    // takes the bar's colour at full strength.
    panelBackground: header?.backgroundColor || '#ffffff',
  };
};

/** `#rrggbb` → `rgba(r, g, b, a)`. Anything else is returned unchanged. */
const withAlpha = (hex, alpha) => {
  const m = /^#([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

/**
 * The bar's background, frosted or not.
 *
 * `amount` is how much of the chosen colour remains: 0 is fully clear, 100 is
 * the solid colour and identical to leaving glass off. A dial rather than a
 * switch because the difference between a barely-there bar over a photograph
 * and a frosted one over text is the entire effect.
 *
 * `backdropFilter` is unprefixed and prefixed — Safari still wants the
 * prefix, and Safari is most of where this look is expected.
 */
export const glassBackground = (style, backgroundColor) => {
  if (!style.glass.enabled) return { backgroundColor };
  const alpha = Math.min(100, Math.max(0, style.glass.amount)) / 100;
  const blur = `blur(${style.glass.blur}px)`;
  return {
    backgroundColor: withAlpha(backgroundColor, alpha),
    backdropFilter: blur,
    WebkitBackdropFilter: blur,
  };
};

/**
 * One look for every button in the bar.
 *
 * Shared so a header with two calls to action does not come out looking like
 * two different websites, which is what happens when each is styled where it
 * is written.
 */
export const buttonSkin = (variant, color, onColor = '#ffffff') => {
  const base = {
    padding: '8px 16px',
    fontWeight: 600,
    display: 'inline-block',
    lineHeight: 1.2,
    cursor: 'pointer',
  };
  switch (variant) {
    case 'outline':
      return { ...base, borderRadius: 8, border: `1px solid ${color}`, background: 'transparent', color };
    case 'ghost':
      return { ...base, borderRadius: 8, border: '1px solid transparent', background: `${color}14`, color };
    case 'pill':
      return { ...base, borderRadius: 999, border: 'none', background: color, color: onColor };
    case 'square':
      return { ...base, borderRadius: 0, border: `1px solid ${color}`, background: color, color: onColor };
    case 'solid':
    default:
      return { ...base, borderRadius: 8, border: 'none', background: color, color: onColor };
  }
};

const FOOTER_DEFAULTS = {
  maxWidth: 1200, gutter: 24, padding: 28, columnGap: 40, linkGap: 10,
  fontSize: 13, align: 'left', minColumnWidth: 180, border: false,
};

/** The footer's measurements, resolved the same way the header's are. */
export const footerStyle = (footer) => {
  const s = footer?.style || {};
  const glass = s.glass || {};
  return {
    maxWidth: num(s.maxWidth, FOOTER_DEFAULTS.maxWidth),
    gutter: num(s.gutter, FOOTER_DEFAULTS.gutter),
    padding: num(s.padding, FOOTER_DEFAULTS.padding),
    columnGap: num(s.columnGap, FOOTER_DEFAULTS.columnGap),
    linkGap: num(s.linkGap, FOOTER_DEFAULTS.linkGap),
    fontSize: num(s.fontSize, FOOTER_DEFAULTS.fontSize),
    align: s.align === 'center' ? 'center' : 'left',
    minColumnWidth: num(s.minColumnWidth, FOOTER_DEFAULTS.minColumnWidth),
    border: Boolean(s.border),
    glass: {
      enabled: Boolean(glass.enabled),
      blur: num(glass.blur, 12),
      amount: num(glass.amount, 60),
    },
  };
};

const headerStyleHelpers = { headerStyle, footerStyle, glassBackground, buttonSkin };
export default headerStyleHelpers;
